<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  channelSync: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const confirmation = ref(null);
const isLoading = ref(false);
const provider = computed(
  () =>
    ({
      telegram_tdlib: t('CONVERSATION.CHANNEL_SYNC.PROVIDERS.telegram_tdlib'),
      whatsapp_evolution: t(
        'CONVERSATION.CHANNEL_SYNC.PROVIDERS.whatsapp_evolution'
      ),
      instagram_native: t(
        'CONVERSATION.CHANNEL_SYNC.PROVIDERS.instagram_native'
      ),
      website: t('CONVERSATION.CHANNEL_SYNC.PROVIDERS.website'),
    })[props.channelSync.provider]
);
const dialogConfig = computed(() => {
  const isRestoring = confirmation.value?.enabled;
  return {
    type: isRestoring ? 'edit' : 'alert',
    title: isRestoring
      ? t('CONVERSATION.CHANNEL_SYNC.RESTORE_TITLE')
      : t('CONVERSATION.CHANNEL_SYNC.DISABLE_TITLE'),
    description: isRestoring
      ? t('CONVERSATION.CHANNEL_SYNC.RESTORE_DESCRIPTION', {
          provider: provider.value,
        })
      : t('CONVERSATION.CHANNEL_SYNC.DISABLE_DESCRIPTION', {
          provider: provider.value,
        }),
    label: isRestoring
      ? t('CONVERSATION.CHANNEL_SYNC.RESTORE')
      : t('CONVERSATION.CHANNEL_SYNC.DISABLE'),
  };
});

const open = () => {
  if (isLoading.value || !props.channelSync.supported) return;
  confirmation.value = {
    conversationId: props.conversationId,
    enabled: !props.channelSync.enabled,
  };
  dialogRef.value.open();
};

const confirm = async () => {
  if (isLoading.value || !confirmation.value) return;
  const requestedState = confirmation.value;
  isLoading.value = true;
  try {
    await store.dispatch('setChannelSyncState', requestedState);
    dialogRef.value?.close();
  } catch {
    useAlert(t('CONVERSATION.CHANNEL_SYNC.FAILED'));
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :type="dialogConfig.type"
    :title="dialogConfig.title"
    :description="dialogConfig.description"
    :confirm-button-label="dialogConfig.label"
    :cancel-button-label="t('CONVERSATION.CHANNEL_SYNC.CANCEL')"
    :is-loading="isLoading"
    @confirm="confirm"
    @close="confirmation = null"
  />
</template>
